/**
 * Layer Definitions
 *
 * Defines the 7 predefined layers for organizing garden objects
 * - Layer IDs, names, colors, and display order
 * - Mapping from object types and categories to layers
 * - Helper functions for layer assignment and ordering
 */

import { getCategoryForObjectType } from '../config/gardenObjectTypes';

// ============================================================================
// LAYER IDS
// ============================================================================

export const LAYER_IDS = {
  BACKGROUND: 'background',
  OUTLINE: 'outline',
  GROUND_COVER: 'ground_cover',
  STRUCTURES: 'structures',
  BEDS: 'beds',
  MISC: 'misc',
  PLANTS: 'plants',
};

// ============================================================================
// LAYER DEFINITIONS (ordered by Z-Index, ascending)
// ============================================================================

export const LAYER_DEFINITIONS = [
  {
    id: 'background',
    name: 'Hintergrund',
    color: '#F59E0B',
    order: 1,
    description: 'Hintergrundbild und Höhenzonen',
  },
  {
    id: 'outline',
    name: 'Garten-Umriss',
    color: '#059669',
    order: 2,
    description: 'Garten-Begrenzung',
  },
  {
    id: 'ground_cover',
    name: 'Bodenbedeckung',
    color: '#84CC16',
    order: 3,
    description: 'Rasen, Terrasse und flächige Bodenbeläge',
  },
  {
    id: 'structures',
    name: 'Wege & Strukturen',
    color: '#6B7280',
    order: 4,
    description: 'Wege, Mauern, Zäune, Gebäude, Teiche',
  },
  {
    id: 'beds',
    name: 'Beete',
    color: '#92400E',
    order: 5,
    description: 'Beete, Hochbeete, Pflanzgefäße',
  },
  {
    id: 'misc',
    name: 'Sonstiges',
    color: '#D1D5DB',
    order: 6,
    description: 'Kompost und weitere Objekte',
  },
  {
    id: 'plants',
    name: 'Pflanzen',
    color: '#10B981',
    order: 7,
    description: 'Alle platzierten Pflanzen',
  },
];

// ============================================================================
// MAPPING: Category → Layer
// ============================================================================

export const CATEGORY_TO_LAYER = {
  strukturen: 'structures',
  gebaeude: 'structures',
  wasser: 'structures',
  pflanzflaechen: 'beds',
  sonstiges: 'misc',
};

// ============================================================================
// GROUND-COVER OBJECT TYPES
// ============================================================================
// Object types that are flächige Bodenbeläge and must render in the
// ground_cover layer regardless of their palette category, so anything placed
// on top (beds, pots, plants) stays visible.
export const GROUND_COVER_OBJECT_TYPES = ['rasen', 'terrasse'];

// ============================================================================
// MAPPING: Wizard data.type → Layer
// ============================================================================

export const WIZARD_TYPE_TO_LAYER = {
  'wizard-background': 'background',
  'height-zone': 'background',
  'height-zone-label': 'background',
  'garden-boundary': 'outline',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Determines the layer for a Fabric.js object
 *
 * Layer assignment priority:
 * 1. Already assigned layer (from data.layer)
 * 2. Wizard objects (via data.type)
 * 3. Plants (via objectType === 'plant')
 * 4. Garden objects (via gardenObjectType → category → layer)
 * 5. Fallback to 'misc'
 *
 * @param {Object} fabricObject - Fabric.js object
 * @returns {string} Layer ID
 */
export function getLayerForObject(fabricObject) {
  if (!fabricObject) return LAYER_IDS.MISC;

  const data = fabricObject.data || {};

  // 1. Already assigned layer?
  if (data.layer && LAYER_DEFINITIONS.find(l => l.id === data.layer)) {
    return data.layer;
  }

  // 2. Wizard objects (data.type)
  if (data.type && WIZARD_TYPE_TO_LAYER[data.type]) {
    return WIZARD_TYPE_TO_LAYER[data.type];
  }

  // 3. Plants (PlantCircle)
  if (fabricObject.objectType === 'plant') {
    return LAYER_IDS.PLANTS;
  }

  // 4. Garden Objects (via category)
  if (data.gardenObjectType) {
    // Special case: flächige Bodenbeläge must render below structures/beds/
    // plants so objects placed on top stay visible. Rasen sits in the
    // Pflanzflächen category, Terrasse in Sonstiges — but both are ground
    // cover by nature, so they override their category → layer mapping.
    if (GROUND_COVER_OBJECT_TYPES.includes(data.gardenObjectType)) {
      return LAYER_IDS.GROUND_COVER;
    }
    const category = getCategoryForObjectType(data.gardenObjectType);
    if (category && CATEGORY_TO_LAYER[category]) {
      return CATEGORY_TO_LAYER[category];
    }
  }

  // 5. Fallback
  return LAYER_IDS.MISC;
}

/**
 * Get the layer order (Z-Index rank) for a layer ID
 *
 * @param {string} layerId - Layer ID
 * @returns {number} Order (1-6) or 999 if not found
 */
export function getLayerOrder(layerId) {
  const layer = LAYER_DEFINITIONS.find(l => l.id === layerId);
  return layer ? layer.order : 999;
}

/**
 * Get layer definition by ID
 *
 * @param {string} layerId - Layer ID
 * @returns {Object|null} Layer definition or null if not found
 */
export function getLayerById(layerId) {
  return LAYER_DEFINITIONS.find(l => l.id === layerId) || null;
}

/**
 * Get all layers sorted by order (ascending)
 *
 * @returns {Array} Array of layer definitions
 */
export function getAllLayersSorted() {
  return [...LAYER_DEFINITIONS].sort((a, b) => a.order - b.order);
}

/**
 * Get all layers sorted by order (descending - for UI display)
 *
 * @returns {Array} Array of layer definitions (highest Z-Index first)
 */
export function getAllLayersReversed() {
  return [...LAYER_DEFINITIONS].sort((a, b) => b.order - a.order);
}

/**
 * Get layer name by ID
 *
 * @param {string} layerId - Layer ID
 * @returns {string} Layer name or 'Unbekannt'
 */
export function getLayerName(layerId) {
  const layer = getLayerById(layerId);
  return layer ? layer.name : 'Unbekannt';
}

/**
 * Get layer color by ID
 *
 * @param {string} layerId - Layer ID
 * @returns {string} Hex color or '#CCCCCC'
 */
export function getLayerColor(layerId) {
  const layer = getLayerById(layerId);
  return layer ? layer.color : '#CCCCCC';
}

/**
 * Sort and apply layer order to the canvas.
 * Primary sort: layer order (background → plants).
 * Secondary sort: zIndexWithinLayer (ascending, within same layer).
 * Grid lines and snap guides always stay first.
 *
 * Mutates canvas._objects in place.
 *
 * @param {fabric.Canvas} canvas
 */
export function applyLayerOrder(canvas) {
  if (!canvas) return;

  const objects = canvas.getObjects();
  const gridLines = objects.filter(o => o.data?.isGridLine || o.data?.isSnapGuide);
  const layerObjects = objects.filter(o => !o.data?.isGridLine && !o.data?.isSnapGuide);

  layerObjects.sort((a, b) => {
    const layerA = getLayerOrder(a.data?.layer || 'misc');
    const layerB = getLayerOrder(b.data?.layer || 'misc');
    if (layerA !== layerB) return layerA - layerB;
    return (a.data?.zIndexWithinLayer ?? 0) - (b.data?.zIndexWithinLayer ?? 0);
  });

  canvas._objects = [...gridLines, ...layerObjects];
}
